"use client";

import { useEffect, useState } from "react";

/** Query-Parameter, mit dem ein Embedder die eingebettete App markiert. */
export const EMBED_PARAM = "embed";

/**
 * Erkennt, ob die App gerade in einem iframe einer anderen Oekosystem-App laeuft
 * (Messenger bettet Kontakte/Kalender ein, ai-chat sein Artefakt-Panel, drive seinen
 * Editor). Eingebettet heisst: KEINE App-Chrome — kein TopNav, kein Cookie-Hinweis,
 * kein Install-Banner, kein Consent-Overlay, kein Splash. Die Huelle stellt der Embedder.
 *
 * Zwei Signale, ODER-verknuepft:
 *  - `window.self !== window.top` (funktioniert immer, auch ohne Zutun des Embedders)
 *  - `?embed=1` (explizit, ueberlebt auch ein spaeteres Aushebeln der iframe-Erkennung)
 *
 * Erst nach dem Mount `true` (SSR-Default `false`) — sonst Hydration-Mismatch.
 *
 * **Und das ist KEIN einzelner Frame, auch wenn es hier bis 2026-09-17 so stand.**
 * An der laufenden `legal.janewaylabs.com/?embed=1` gemessen (headless Chrome,
 * leerer Cache, drei Laeufe): der Server liefert die Kopfzeile mit, sie steht 145
 * bis 194 ms sichtbar im Bild und nimmt beim Abraeumen 56 px Hoehe mit — der
 * „Versatz", den der Betreiber am Seitenpanel gemeldet hat.
 *
 * Reparieren laesst sich das hier NICHT: der Kopf ist gezeichnet, sobald der
 * Parser sein Markup erreicht, und jede Client-Entscheidung faellt danach. Wer
 * ihn vor dem ersten Bild wegnehmen will, braucht CSS plus ein Inline-Skript vor
 * dem Markup — `lib/embed-vorpaint.ts`, von `AppLayout` ausgegeben. Dieser Hook
 * bleibt, was er ist: die Entscheidung fuer den React-Baum nach der Hydration.
 */
export function useIsEmbedded(): boolean {
  /*
   * ── WARUM HIER `useState` + `useEffect` STEHT UND NICHT `useSyncExternalStore`
   *
   * eslint meldet an der Zeile `setEmbedded(true)` unten
   * `react-hooks/set-state-in-effect`. Der Befund ist echt, und die saubere
   * Umsetzung waere `useSyncExternalStore(nieAendern, jetzt, () => false)`.
   * Sie ist am 2026-09-17 gebaut und Seite an Seite gemessen worden (isolierte
   * Kopie, `?embed=1`, Folge der gerenderten Werte je Render):
   *
   *     Serverbild                 <span>false</span>  ==  <span>false</span>
   *     SSR → hydrateRoot          [false, true]       ==  [false, true]
   *     frischer createRoot-Mount  [false, true]       !=  [true]
   *
   * Fuer den Weg, den alle 25 Apps beim Laden gehen — Serverbild plus
   * Hydration — ist es Zeichen fuer Zeichen dasselbe. Der dritte Pfad, ein
   * Baustein der erst bei einer Client-Navigation frisch mountet, ist anders:
   * `getSnapshot` laeuft schon im ersten Render, der Wert ist sofort `true`
   * statt einen Render spaeter.
   *
   * **Das waere eine Verbesserung** — ein Render weniger und kein
   * `false`→`true`-Zucken bei spaet gemounteten Bausteinen. Es ist trotzdem
   * eine Verhaltensaenderung an einem Hook in 25 Apps, und was die Apps auf
   * diesem dritten Pfad tun, ist nicht gemessen: im Showroom kommt er nicht
   * vor. Deshalb bewusst aufgeschoben (Betreiber-/Lead-Entscheidung
   * 2026-09-17, waehrend eines laufenden Prod-Deploys), nicht uebersehen.
   *
   * Wer ihn holt, misst die drei Zeilen oben erneut und dazu die Apps, nicht
   * nur den Hook.
   */
  const [embedded, setEmbedded] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const inFrame = window.self !== window.top;
    const flagged = new URLSearchParams(window.location.search).get(EMBED_PARAM) === "1";
    if (inFrame || flagged) setEmbedded(true);
  }, []);

  return embedded;
}
